import { WinAnimator, WinAnimatorAttributes } from "../GameComponents/WinAnimator";
import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbol } from "../GameComponents/Symbol";
import { Symbols } from "../GameComponents/Symbols";
import { Payline } from "../GameComponents/Payline";
import { Paylines } from "../GameComponents/Paylines";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Communication } from "neo-games";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { Tweens } from "../Components/Effects/Tweens";
import { BetManager } from "../Managers/BetManager";
import { AppContext } from "../../Common/AppContext";
import { ImageTextCounterElement2 } from "./ImageTextCounterElement2";
import { BaseView } from "../GameComponents/Base/BaseView";
import { ISymbolTeasingAttributes, SymbolTeasingActions, TeasingComponent } from "./TeasingComponent";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { Settings } from "./Settings";
import { MachineConfigurationAttributes, ReelConfigurationAttributes } from "../Components/Configuration";
import { ModuleManager } from "../Managers/ModuleManager";
import { GameManager } from "../Managers/GameManager";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    // this is a FS animator
    private isFS: boolean = false;

    private _paylines: Paylines;

    protected elementInstance: Array<Object> = [];

    private _paylineWinDelay: number = 60; /* Delay between win lines */
    private _paylineWinEndDelay: number = 90; /* Delay after win lines, before cycle */
    private _paylineCycleDelay: number = 90; /* Delay between cycle lines */

    private _paylineEvaluation: string = WinAnimatorExtendedAttributes.PAYLINE_EVALUATION_TYPES.TOGETHER;

    private _winScaleConstantAdditive: number = 1;
    private _winScaleConstantMultiplicative: number = 0;
    private _winScaleDuration: number = 45;
    private _winScaleEase: string = "backOut";
    private _winScaleKeyTimes: Array<number> = [0, 0, 1, 1];

    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;

    private _customData: Object = {};

    private _cycleAnimationName: string = "Value";

    private _counterPlayed: boolean = false;

    protected counter: ImageTextCounterElement2;
    protected currentCounterStep: number;

    private starTimerStack: Array<FrameTimerEventEmitter> = [];

    private wasForce: boolean;
    private _scatterDelay: number = 500;
    private _scatterTimeout: number = null;

    // pre roll height in pixels
    private _preRoll: number = 180;
    // pre roll speed in frames
    private _preRollSpeed: number = 10;
    // delay to start rotation in frames
    private _startDelay: number = 2;

    protected static readonly CaseNormal = "NORMAL";
    protected static readonly CaseWin = "WIN";
    protected static readonly CaseWin10 = "WIN_10";
    protected static readonly CaseNoWin = "NOWIN";
    protected static readonly CaseHighWin = "HIGHWIN";
    protected static readonly CaseReset = "RESET";
    protected static readonly CaseIdle = "IDLE";

    protected static readonly CasePrefixNormal = "";
    protected static readonly CasePrefixFreeSpins = "FS_";

    protected static readonly CaseSuffixCycle = "_CYCLE";
    protected static readonly CaseSuffixOutro = "_OUTRO";

    protected static readonly ShowFirstWinsStep = "showFirstWinsStep";
    protected static readonly EndFirstWinsStep = "endFirstWinsStep";
    protected static readonly FinishWin = "finishWin";

    initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView | FreespinsView,
    ): void {
        this._paylines = paylines;

        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (!this.value && this._cycleAnimationName && this.childs[this._cycleAnimationName]) {
            this.value = this.childs[this._cycleAnimationName];
            this.animationsContainer.addChild(this.value);
        }

        if (parent) {
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });
        }

        // Add paylines as child due to reorder containers
        //this.addChild(this._paylines);
    }

    protected createAnimations(): void {
        super.createAnimations();

        for (let animation of this.animations) {
            animation.on("softOutro", this.softOutro.bind(this));
            animation.on("playHWLuciferAnimation", this.playHWLuciferAnimation.bind(this));
        }
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY)) {
            this._paylineWinDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY)) {
            this._paylineWinEndDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_END_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY)) {
            this._paylineCycleDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_EVALUATION)) {
            let eValue = node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_EVALUATION).toLowerCase();
            for (let val in WinAnimatorExtendedAttributes.PAYLINE_EVALUATION_TYPES) {
                if (WinAnimatorExtendedAttributes.PAYLINE_EVALUATION_TYPES[val] == eValue) {
                    this._paylineEvaluation = WinAnimatorExtendedAttributes.PAYLINE_EVALUATION_TYPES[val];
                }
            }
        }

        // Win counter scaling
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_CONSTANT_ADDITIVE)) {
            this._winScaleConstantAdditive = parseFloat(
                node.getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_CONSTANT_ADDITIVE),
            );
        }
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_CONSTANT_MULTIPLICATIVE)) {
            this._winScaleConstantMultiplicative = parseFloat(
                node.getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_CONSTANT_MULTIPLICATIVE),
            );
        }
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_DURATION)) {
            this._winScaleDuration = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_DURATION));
        }
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_EASE)) {
            this._winScaleEase = node.getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_EASE);
        }
        if (node.hasAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_KEY_TIMES)) {
            let newTimes: Array<number> = node
                .getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_KEY_TIMES)
                .split(";")
                .map((value) => parseFloat(value));
            if (newTimes && newTimes.length == 4) {
                this._winScaleKeyTimes = newTimes;
            } else {
                this.logError(
                    "Incorrect value of " +
                        WinAnimatorExtendedAttributes.WIN_SCALE_KEY_TIMES +
                        " attribute: '" +
                        node.getAttribute(WinAnimatorExtendedAttributes.WIN_SCALE_KEY_TIMES) +
                        "'.\n" +
                        "Expected four semicolon-separated numeric values ranged <0;1> in non-descending order, e.g. '0;0;0.5;1'",
                );
            }
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION)) {
            this._cycleAnimationName = node.getAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION);
        }

        if (node.getAttribute(WinAnimatorExtendedAttributes.IS_FS_ANIMATOR) == "true") {
            this.isFS = true;
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)) {
            this._preRoll = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL));
        }

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)) {
            this._preRollSpeed = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED));
        }

        if (node.hasAttribute(MachineConfigurationAttributes.START_DELAY)) {
            this._startDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.START_DELAY));
        }
    }

    public hide() {
        this.visible = false;
        this.symbolsContainer.visible = false;
        this.noWinSymbolsContainer.visible = false;
        this.animationsContainer.visible = false;
    }

    public show() {
        this.visible = true;
        this.symbolsContainer.visible = true;
        this.noWinSymbolsContainer.visible = true;
        this.animationsContainer.visible = true;
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number, force: boolean = false): void {
        //super.onReelStopped(reelsData, reelIndex);

        this.wasForce = force;
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.addCurrentSymbol(reelIndex, i);
        }

        if (this.elementInstance["teasing"] != null) {
            let sta: Array<Array<ISymbolTeasingAttributes>>;
            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponent) {
                    let symbolsTeasingAttributes = teasingComponent.checkSymbolsPerReel(reelIndex);
                    sta = symbolsTeasingAttributes;
                }, this);
            }

            if (!force) {
                for (let i = 0; i <= reelIndex; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        if (this.currentSymbols[i][j] != null) {
                            if (typeof sta !== "undefined") {
                                switch (sta[i][j].action) {
                                    case SymbolTeasingActions.NORMAL:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                        }
                                        break;
                                    case SymbolTeasingActions.IMPACT:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                        }
                                        break;
                                    case SymbolTeasingActions.IMPACT_TEASE:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactTease)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpactTease);
                                        }
                                        break;
                                    case SymbolTeasingActions.IMPACT_NOWIN:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactNowin)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpactNowin);
                                        }
                                        break;
                                    case SymbolTeasingActions.NOWIN:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                                        }
                                        break;
                                    case SymbolTeasingActions.TEASE:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseTeaser)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseTeaser);
                                        }
                                        break;
                                }
                            } else {
                                if (i == reelIndex && this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                } else if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            } else {
                for (let i = 0; i <= reelIndex; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        if (this.currentSymbols[i][j] != null) {
                            if (typeof sta !== "undefined") {
                                switch (sta[i][j].action) {
                                    case SymbolTeasingActions.IMPACT:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                        }
                                        break;
                                    case SymbolTeasingActions.NORMAL:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                        }
                                        break;
                                    case SymbolTeasingActions.IMPACT_TEASE:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactTease)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpactTease);
                                        }
                                        break;
                                    case SymbolTeasingActions.IMPACT_NOWIN:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactNowin)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseImpactNowin);
                                        }
                                        break;
                                    case SymbolTeasingActions.NOWIN:
                                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                                        }
                                        break;
                                }
                            } else {
                                if (i == reelIndex && this.allowSymbolGoToCase(i, j, Symbol.CaseImpact)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseImpact);
                                } else if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  WINS
    //******************************************************************************************************************

    showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.resetCounter();

        let ret = super.showWins(wins, win);

        if (wins == null || wins.length === 0) {
            this.startStarAnimation();
        }

        if (win > 0) {
            if (this.currentMultiplierIndex > 1) {
                // priority: current FS prefix + HIGH + number -> current FS prefix + HIGH -> HIGH + number -> HIGH -> other FS prefix + HIGH + number + other FS prefix + HIGH
                this.setActiveElementCaseArray([
                    (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin,
                    (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseWin,
                    WinAnimatorExtended.CaseHighWin + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighWin,
                    (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin +
                        this.currentMultiplierIndex,
                    (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin,
                ]);

                this.setActiveElementCase("HIGHWIN_FOREGROUND");
                this.shakeWithSymbolsPaylines();
            } else {
                // rnd lucifer animation
                let min = Math.ceil(1);
                let max = Math.floor(3);
                let rnd = "_" + (Math.floor(Math.random() * (max - min)) + min);

                let rnd_sound = "_" + (Math.floor(Math.random() * 11) + 1);

                // priority: current FS prefix + WIN -> WIN -> other FS prefix + WIN
                if (this.currentMultiplierIndex == 0) {
                    this.setActiveElementCaseArray([
                        (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                            WinAnimatorExtended.CaseWin +
                            rnd +
                            rnd_sound,
                        WinAnimatorExtended.CaseWin,
                        (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                            WinAnimatorExtended.CaseWin,
                    ]);
                } else {
                    this.setActiveElementCaseArray([
                        (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                            WinAnimatorExtended.CaseWin10 +
                            rnd +
                            rnd_sound,
                        WinAnimatorExtended.CaseWin,
                        (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                            WinAnimatorExtended.CaseWin,
                    ]);
                }
            }
            //PlayList.stop("SND_BaseLoop", 30);
        } else {
            // priority: current FS prefix + NOWIN -> NOWIN -> other FS prefix + NOWIN
            this.setActiveElementCaseArray([
                (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                    WinAnimatorExtended.CaseNoWin,
                WinAnimatorExtended.CaseNoWin,
                (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                    WinAnimatorExtended.CaseNoWin,
            ]);
        }

        return ret;
    }

    private shakeWithSymbolsPaylines(): void {
        Tweens.positionTween(this.noWinSymbolsContainer, {
            tweenDelay: 30,
            tweenX: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            tweenY: [0, 20, 0, -20, 0, 10, 0, -10, 0, 5, 0, -5, 0, 2.5, 0, -2.5, 0, 1, 0, -1, 0, 0.5, 0, -0.5, 0],
            tweenDuration: 50,
        });

        Tweens.positionTween(this.symbolsContainer, {
            tweenDelay: 30,
            tweenX: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            tweenY: [0, 20, 0, -20, 0, 10, 0, -10, 0, 5, 0, -5, 0, 2.5, 0, -2.5, 0, 1, 0, -1, 0, 0.5, 0, -0.5, 0],
            tweenDuration: 50,
        });

        Tweens.positionTween(this._paylines, {
            tweenDelay: 30,
            tweenX: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            tweenY: [0, 20, 0, -20, 0, 10, 0, -10, 0, 5, 0, -5, 0, 2.5, 0, -2.5, 0, 1, 0, -1, 0, 0.5, 0, -0.5, 0],
            tweenDuration: 50,
        });
    }

    protected showFirstWins(): void {
        /*
        if (this.currentMultiplierIndex === 0 || this.currentMultiplierIndex === 1) {
            this.currentCounterStep = 0;

            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep, 15);
        } else {*/
        super.showFirstWins();
        //}
    }

    protected putSymbolsOnTop(symbols: Array<Symbol>): void {
        while (this.symbolsContainer.children[0]) {
            this.noWinSymbolsContainer.addChild(this.symbolsContainer.children[0]);
        }
        symbols.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
    }

    /*
    protected showFirstWinsStep(): void {
        const win = this.currentWins[this.currentCounterStep];

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }

        if(this._paylineEvaluation === WinAnimatorExtendedAttributes.PAYLINE_EVALUATION_TYPES.SEPARATE) {
            this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
            const symbols = this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);
            this.putSymbolsOnTop(symbols);
        }
        else {
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                this.winPaylineGoToCase(i, Payline.CaseWin);
            }
            this.currentCounterStep = this.currentWins.length-1;
        }

        if(win.amount > 0) {
            this.runCounterStep();
        }

        this.currentCounterStep++;

        this.startTimer((this.currentCounterStep < this.currentWins.length) ? WinAnimatorExtended.ShowFirstWinsStep : WinAnimatorExtended.FinishWin, this._paylineWinDelay);
    }*/

    protected endFirstWinsStep(): void {
        this.currentCounterStep = null;
        this.timerStatus = null;

        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        // PlayList.stop('SND_BaseLoop', 30);
        // PlayList.loop("SND_CycleLoop", 30);
        /*
                let isScatter = false;
                if(this.currentWins) {
                    this.currentWins.forEach(win => {
                        if(win.trigger === Communication.Game.WinTrigger.Scatter) {
                            isScatter = true;
                        }
                    });
                }
        */
        this.cycleWins();
    }

    protected finishWin(): void {
        this.emit(WinAnimator.FinishWinCalculation);

        this.runCounterOutro();

        let endDelay: number = this._paylineWinEndDelay;

        // if scatter, no delay
        if (this.currentWins) {
            this.currentWins.forEach((win) => {
                if (win.trigger === Communication.Game.WinTrigger.Scatter) {
                    endDelay = 0;
                }
            });
        }

        this.startTimer(WinAnimatorExtended.EndFirstWinsStep, endDelay);
    }

    protected finishFirstWins(): void {
        // PlayList.stop('SND_BaseLoop', 30);
        // PlayList.loop("SND_CycleLoop", 30);

        //if (this.currentMultiplierIndex > 1) {
        super.finishFirstWins();
        //}
    }

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        let skipCycle: boolean = false;
        if (
            this.currentWins[this.currentCycle].trigger === Communication.Game.WinTrigger.Scatter &&
            this.currentWins[this.currentCycle].type === Communication.Game.WinType.freeSpins
        ) {
            this.allSymbolsGoToCase(Symbol.CaseNormal, true);
            skipCycle = true;
        } else {
            PlayList.play("SND_BaseRecapWinline");
            // PlayList.loop("SND_CycleLoop");

            // e.g. "WIN" -> "WIN_CYCLE", "HIGH1" -> "HIGH1_CYCLE"
            //this.setActiveElementCase(this.getActiveElementCase() + WinAnimatorExtended.CaseSuffixCycle);

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }

            let symbols = this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);

            this.putSymbolsOnTop(symbols);

            this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;
        }

        if (skipCycle) {
            this.startTimer(WinAnimator.CycleWins, 1);
        } else {
            // if(this.currentWins.length > 1) {
            this.startTimer(WinAnimator.CycleWins, this._paylineCycleDelay);
            // }
        }
    }

    protected getCycleWinsDelay(): number {
        return 90;
    }

    protected stopAnimation(): void {
        this.setActiveElementCase("HIDING_QUICK");

        super.stopAnimation();
    }

    protected onAnimationFinished(): void {
        this.setActiveElementCase("HIDING");

        super.onAnimationFinished();
    }

    protected onAnimationComplete(): void {
        super.onAnimationComplete();
    }

    protected softOutro(): void {
        if (ModuleManager.freespinsActive()) {
            if (this.currentMultiplierIndex > 1) {
            }
        }
    }
    protected playHWLuciferAnimation(): void {
        this._activeElements.forEach((element) => {
            if (element.hasCase(WinAnimatorExtended.CaseHighWin)) {
                element.goToCase(WinAnimatorExtended.CaseHighWin);
            }
            if (element.hasCase("HIGHWIN_FOREGROUND")) {
                element.goToCase("HIGHWIN_FOREGROUND");
            }
        });

        this.shakeWithSymbolsPaylines();
    }

    reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        this.stopStarAnimation();

        if (this._scatterTimeout) {
            clearTimeout(this._scatterTimeout);
            this._scatterTimeout = null;
        }

        // resets to current case w/o "_CYCLE" suffix + "_OUTRO" suffix; OR sets to "RESET"
        let activeCase = this.getActiveElementCase();
        let caseReg = new RegExp(WinAnimatorExtended.CaseSuffixCycle + "$");
        if (caseReg.test(activeCase)) {
            this.setActiveElementCaseArray([
                activeCase.replace(caseReg, "") + WinAnimatorExtended.CaseSuffixOutro,
                WinAnimatorExtended.CaseReset,
            ]);
        } else {
            // this.setActiveElementCaseArray([ activeCase, activeCase + WinAnimatorExtended.CaseSuffixOutro, WinAnimatorExtended.CaseReset ] );

            // set RESET CASE for BackgroundBottom (particles) only
            this._activeElements.forEach((element) => {
                if (element instanceof SwitchElement) {
                    let SElement: SwitchElement = element;

                    if (SElement != null && SElement.name === "TeasingWilds") {
                        let o: Object = SElement.getChildElementByName("BackgroundBottom");

                        if (o != null && o instanceof SwitchElement) {
                            o.goToCase(WinAnimatorExtended.CaseReset);
                        }
                    }
                }
            });
        }

        let outroPlayed = false;

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.runCounterOutro(true);
                outroPlayed = true;
                break;
            case WinAnimatorExtended.FinishWin:
                this.runCounterOutro(true);
                outroPlayed = true;
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
        }

        if (this.currentCounterStep != null) {
            this.currentCounterStep = null;

            if (!outroPlayed) {
                this.runCounterOutro(true);
            }
        }

        if (showLastSymbols) {
            this.startStarAnimation();
        }

        // PlayList.stop("SND_CycleLoop");
        // PlayList.loop("SND_BaseLoop", 30);
        if (spin) {
            for (let reelIndex = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                for (let rowIndex = 0; rowIndex < this.currentSymbols[reelIndex].length; rowIndex++) {
                    const symbol = this.currentSymbols[reelIndex][rowIndex];

                    if (symbol != null && Settings.SymbolsNoTrim.indexOf(symbol.id + 1) != -1) {
                        if (this.elementInstance["wildContainer"] != null) {
                            //this.currentSymbols[reelIndex][rowIndex].visible = false;
                            // symbol.alpha = 0.5;
                            this.currentSymbols[reelIndex][rowIndex] = null;
                            this.elementInstance["wildContainer"].addChild(symbol);

                            if (
                                symbol.getCurrentCase() !== Symbol.CaseNormal ||
                                symbol.getCurrentCase() !== Symbol.CaseIdle
                            ) {
                                symbol.goToCase(Symbol.CaseNormal);
                            }

                            symbol.goToCase(Symbol.CaseRotation);

                            Tweens.positionTween(symbol, {
                                tweenDelay: this._startDelay * reelIndex,
                                tweenX: [symbol.x, symbol.x, symbol.x],
                                tweenY: [symbol.y, symbol.y - this._preRoll, symbol.y],
                                tweenDuration: this._preRollSpeed * 2,
                                callbackFunction: (): void => {
                                    this.symbolsContainer.addChild(symbol);
                                    symbol.goToCase(Symbol.CaseEmpty);
                                    this.currentSymbols[reelIndex][rowIndex] = symbol;
                                },
                            });
                        }
                    }
                }
            }
        }

        PlayList.stop("SND_CycleLoop");
        // PlayList.loop("SND_BaseLoop", 30);

        return super.reset(showLastSymbols);
    }

    resetAnimations(): void {
        super.resetAnimations();

        this.setActiveElementCase("SHOW_FALSE");
    }

    public respinReset(respin: number): boolean {
        return true;
    }

    protected idle(): void {
        this.setActiveElementCaseArray([
            (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                WinAnimatorExtended.CaseIdle,
            WinAnimatorExtended.CaseNoWin,
            (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                WinAnimatorExtended.CaseIdle,
        ]);
        super.idle();
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols = super.winSymbolsGoToCase(winIndex, caseType, useValue);
        if (useValue && symbols.length > 0 && win.amount > 0) {
            this.value.position.x = symbols[0].position.x + symbols[0].offsetX + this.symbolWidth / 2;

            this.value.position.y = symbols[0].position.y + symbols[0].offsetY + this.symbolHeight / 2;
            if (symbols[0].id === 7) {
                // remove win value offset for symbol 8
                this.value.position.y -= 80;
            }
            this.value.scale.x = this.value.scale.y = 1;
            this.value.visible = true;
            this.value.play(win.amount);
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.resetCounter();

        super.showInitWins(wins, win);

        this.setActiveElementCase("INTRO_GAME", false, 30);

        this.startStarAnimation();

        if (win > 0) {
            if (this.currentMultiplierIndex > 1) {
                // priority: current FS prefix + HIGH + number -> current FS prefix + HIGH -> HIGH + number -> HIGH -> other FS prefix + HIGH + number + other FS prefix + HIGH
                this.setActiveElementCaseArray([
                    (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin +
                        this.currentMultiplierIndex,
                    (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin,
                    WinAnimatorExtended.CaseHighWin + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighWin,
                    (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin +
                        this.currentMultiplierIndex,
                    (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseHighWin,
                ]);
            } else {
                // priority: current FS prefix + WIN -> WIN -> other FS prefix + WIN
                this.setActiveElementCaseArray([
                    (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseWin,
                    WinAnimatorExtended.CaseWin,
                    (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                        WinAnimatorExtended.CaseWin,
                ]);
            }
            PlayList.stop("SND_BaseLoop", 30);
        } else {
            // priority: current FS prefix + NOWIN -> NOWIN -> other FS prefix + NOWIN
            this.setActiveElementCaseArray([
                (this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                    WinAnimatorExtended.CaseNoWin,
                WinAnimatorExtended.CaseNoWin,
                (!this.isFS ? WinAnimatorExtended.CasePrefixFreeSpins : WinAnimatorExtended.CasePrefixNormal) +
                    WinAnimatorExtended.CaseNoWin,
            ]);
        }
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    private setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s;
        this._activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    private getActiveElementCase(): string {
        return this._activeElementsCase;
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                //this.showFirstWinsStep();
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.endFirstWinsStep();
                break;
            case WinAnimatorExtended.FinishWin:
                this.finishWin();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    protected resetCounter(): void {
        if (!this.animations || !this.animations[this.getAnimationIndex()]) {
            return;
        }
        Tweens.removeTweens(this.animations[this.getAnimationIndex()], "all");
        this.animations[this.getAnimationIndex()].visible = false;
    }

    protected runCounterStep(): void {
        if (!this.animations || !this.animations[this.getAnimationIndex()]) {
            return;
        }
        // play sound
        //PlayList.play('SND_BasePayline' + Math.min(this.currentCounterStep + 1, 16));
        //PlayList.play('SND_BasePayline');

        // calculate from and to values
        let amountFrom: number = 0;
        let amountTo: number = 0;
        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                /*
                if (i < this.currentCounterStep) {
                    amountFrom += this.currentWins[i].amount;
                }*/
                amountTo += this.currentWins[i].amount;
            }
        }

        // start animation from->to
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.animations[this.getAnimationIndex()].play(
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize)),
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize)),
            );
        } else {
            ImageTextCounterElement.Round = false;
            this.animations[this.getAnimationIndex()].play(amountTo, amountFrom);
        }
        this.animations[this.getAnimationIndex()].visible = true;

        // win scale animation
        Tweens.removeTweens(this.animations[this.getAnimationIndex()], "all");

        if (this._winScaleConstantMultiplicative == 0) {
            this.animations[this.getAnimationIndex()].scale.x = this._winScaleConstantAdditive;
            this.animations[this.getAnimationIndex()].scale.y = this._winScaleConstantAdditive;
            return;
        }

        let scaleFrom =
            this._winScaleConstantAdditive +
            (this._winScaleConstantMultiplicative * this.currentCounterStep) / this.currentWins.length;
        let scaleTo =
            this._winScaleConstantAdditive +
            (this._winScaleConstantMultiplicative * (this.currentCounterStep + 1)) / this.currentWins.length;

        Tweens.scaleTween(this.animations[this.getAnimationIndex()], {
            tweenValues: [
                new PIXI.Point(scaleFrom, scaleFrom),
                new PIXI.Point(scaleFrom, scaleFrom),
                new PIXI.Point(scaleTo, scaleTo),
                new PIXI.Point(scaleTo, scaleTo),
            ],
            keyTimes: this._winScaleKeyTimes,
            tweenDuration: this._winScaleDuration,
            tweenEase: this._winScaleEase,
        });

        this._counterPlayed = true;
    }

    public setCustomData(value: any) {
        this._customData = value;
    }

    protected runCounterOutro(reset: boolean = false): void {
        if (!this.animations || !this.animations[this.getAnimationIndex()] || !this._counterPlayed) {
            return;
        }
        Tweens.removeTweens(this.animations[this.getAnimationIndex()], "all");

        let scale =
            this._winScaleConstantAdditive +
            (this._winScaleConstantMultiplicative * (reset ? this.currentWins.length : this.currentCounterStep)) /
                this.currentWins.length;
        this.animations[this.getAnimationIndex()].scale.x = scale;
        this.animations[this.getAnimationIndex()].scale.y = scale;
        this.animations[this.getAnimationIndex()].winValue = this.currentWin;
        this.animations[this.getAnimationIndex()].goToOutro();

        this._counterPlayed = false;
    }

    /**
     * Finish high win animations and start cycle win animations.
     * @method finishHighWins
     * @return void
     */
    private finishHighWins(): void {
        // PlayList.loop("SND_CycleLoop", 120);
        this.onAnimationFinished();
        this.emit(WinAnimator.FinishWinAnimator);
    }

    public getCurrentMultiplierIndex(): number {
        return this.currentMultiplierIndex;
    }

    //******************************************************************************************************************
    //  STAR ANIMATION
    //******************************************************************************************************************

    private startStarAnimation(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            let row = this.currentSymbols[i];
            for (let j = 0; j < row.length; j++) {
                let symbol = row[j] as Symbol;
                var children = symbol.getChildElements();

                for (let key in children) {
                    if (key.lastIndexOf("Star", 0) === 0) {
                        let star = children[key] as TimelineElement;
                        if (star !== null) {
                            let timer = CreateFrameTimer("symbol" + i + j + "_" + key);
                            this.starTimerStack.push(timer);

                            let rand = Math.floor(Math.random() * 100 * ((i + 1) * (j + 1))) + 1;

                            timer.start(rand);
                            timer.on(FrameTimerEvents.Complete, () => {
                                star.play();
                            });
                        }
                    }
                }
            }
        }
    }

    private stopStarAnimation(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            let row = this.currentSymbols[i];
            for (let j = 0; j < row.length; j++) {
                let symbol = row[j] as Symbol;
                var children = symbol.getChildElements();

                for (let key in children) {
                    if (key.lastIndexOf("Star", 0) === 0) {
                        let star = children[key] as TimelineElement;
                        if (star !== null) {
                            star.stop();
                        }
                    }
                }
            }
        }

        for (let i = 0, len = this.starTimerStack.length; i < len; i++) {
            let timer = this.starTimerStack.pop();
            timer.stop();
        }
    }

    protected getIdleDelay(): number {
        return 160;
    }

    protected getIdleActiveElementsCount(): number {
        return 2;
    }

    public isHighWin(win: number): boolean {
        return win / BetManager.Bet >= this.multipliers[2];
    }
}

export const WinAnimatorExtendedAttributes = {
    /*
     * List of child SwitchElements which should be set to different cases at different times (e.g. "WIN", "NOWIN", "CYCLE"...) if possible
     * @property ActiveElements
     * @type string
     * @default ""
     */
    ACTIVE_ELEMENTS: "ActiveElements",

    PAYLINE_DURATION: "PaylineDuration",
    PAYLINE_WIN_DELAY: "PaylineWinDelay",
    PAYLINE_WIN_END_DELAY: "PaylineWinEndDelay",
    PAYLINE_CYCLE_DELAY: "PaylineCycleDelay",
    PAYLINE_EVALUATION: "PaylineEvaluation",
    PAYLINE_EVALUATION_TYPES: {
        SEPARATE: "separate",
        TOGETHER: "together",
    },

    CYCLE_ANIMATION: "CycleAnimation",

    IS_FS_ANIMATOR: "FreeSpinAnimator",

    /**
     * For 0th-multiplier (regular win) counter, in each step (win), the counter goes through scaling animation:
     *      ScaleTween = ScaleStart;ScaleStart;ScaleEnd;ScaleEnd
     *      (this allows tweaking through keytimes)
     * Where:
     *      ScaleStart = A + M * current / max
     *      ScaleEnd = A + M * (current+1) / max
     * Where:
     *      A = Additive Constant
     *      M = Multiplicative Constant
     *      current = current win step
     *      max = total win step
     */

    /*
     * @property WinScaleConstantAdditive
     * @type number
     * @default 1
     */
    WIN_SCALE_CONSTANT_ADDITIVE: "WinScaleConstantAdditive",
    /*
     * @property WinScaleConstantMultiplicative
     * @type number
     * @default 0
     */
    WIN_SCALE_CONSTANT_MULTIPLICATIVE: "WinScaleConstantMultiplicative",
    /*
     * @property WinScaleDuration
     * @type number
     * @default 30
     */
    WIN_SCALE_DURATION: "WinScaleDuration",
    /*
     * @property WinScaleEase
     * @type string
     * @default "backOut"
     */
    WIN_SCALE_EASE: "WinScaleEase",
    /*
     * Key times of regular win scale animations;
     * @property WinScaleKeyTimes
     * @type string
     * @default "0;0;1;1"
     */
    WIN_SCALE_KEY_TIMES: "WinScaleKeyTimes",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/